import '@babel/polyfill';
import { LicenseManager } from '@ag-grid-enterprise/core'; // eslint-disable-line
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'; // eslint-disable-line
import * as Sentry from '@sentry/react';
// imports modules from common package
import {
	Authentication,
	Chatbot,
	FeatureFlags,
	Layout,
	Modal,
	Redirect,
	Toast,
	UI,
	Utils,
} from 'common';
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import MainView from './components/MainView.component';
import ProductsCommunication from './components/utils/ProductsCommunication';

Sentry.init({
	dsn: 'https://2bf0cdb2c7121a2c231715a123b5bbb1@o106853.ingest.us.sentry.io/4509831239499776',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment:
		process.env.NODE_ENV === 'production' ? 'products' : 'products-dev',
});

const { Viewport } = Layout;
const { ModalContainer } = Modal;
const { Analytics, Auth0Util, Session, StyleLoader, Navigation, Util } = Utils;
const { Spinner } = UI;
const { SignIn } = Authentication;
const { ChatbotInitializer, SierraInitializer, SierraChatLauncher } = Chatbot;
const { InitializeLaunchDarkly } = FeatureFlags;
const { EndiciaRedirect } = Redirect;
const { ToastProvider } = Toast;

LicenseManager.setLicenseKey(__AG_GRID_LICENSE__);

export default class App extends Component {
	constructor(props) {
		super(props);

		const page = 'Products';

		// Check if on Endicia domain - skip auth calls but still load styles
		const redirectToStamps = Util.isEndiciaDomain();

		// initialize Session with sessionTimeout handler
		Session.init(
			{
				Page: page,
			},
			() => {
				SierraInitializer.logout();
				ChatbotInitializer.logout();

				if (Auth0Util.isEligibleForAuth0()) {
					Session.signOut(() => {
						Auth0Util.signOut();
					});
				} else {
					this.setState({
						showSignIn: true,
					});
				}
			}
		);

		// initialize ProductsCommunication
		ProductsCommunication.init({
			resetTimeout: Session.resetTimeout,
			triggerTimeout: Session.triggerTimeout,
		});

		this.state = {
			data: {
				InternalAccountInfo: true,
			},
			page,
			showMask: false,
			showSignIn: false,
			stylesLoaded: false,
			auth0Error: false,
		};

		// Skip verifyAuthAndLoad for Endicia domain, but still load styles
		if (redirectToStamps) {
			StyleLoader.loadTheme(() => {
				// Directly set state in constructor (setState not allowed here)
				this.state.showMask = false;
				this.state.stylesLoaded = true;
			});
		} else {
			this.verifyAuthAndLoadSession();
		}
	}

	sessionLoadCallback = async () => {
		const sessionData = Session.getData();
		const { page } = this.state;
		const { isSignedIn, settings } = sessionData;
		const optimizelyClient = createInstance({ sdkKey: settings.OptimizelyKey });
		const isAccountVerificationRequired =
			Session.isAccountVerificationRequired();

		// update document title
		document.title = `Stamps.com | ${page}`;

		// load the theme bundle
		StyleLoader.loadTheme(
			this.setState({ showMask: true, stylesLoaded: true })
		);

		const completeAuth = Util.isInRoute('completeAuth') === true;

		if (!isSignedIn && completeAuth) {
			const completeAuthResponse = await Auth0Util.completeAuth();

			if (completeAuthResponse?.isSuccessful === true) {
				Navigation.redirect(completeAuthResponse.url);
				return;
			} else {
				this.setState({
					auth0Error: true,
				});
			}
		}

		if (isAccountVerificationRequired) {
			await Navigation.redirectToAccountVerify();
		}

		// initialize Analytics
		Analytics.set(sessionData);
		const LDProvider = await InitializeLaunchDarkly();

		this.setState({
			showMask: false,
			showSignIn: isSignedIn !== true,
			optimizelyClient,
			analyticsMetadata: Analytics.metadata,
			LDProvider,
		});
	};

	signInCallback = ({ status }) => {
		if (status) {
			this.setState(
				{
					showMask: true,
					showSignIn: false,
				},
				this.verifyAuthAndLoadSession
			);
			ChatbotInitializer.resetChatMessengerUserPrefs();
		}
	};

	verifyAuthAndLoadSession = () => {
		const { data } = this.state;
		Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
	};

	render() {
		// Redirect to Stamps.com when on Endicia domain - must be the very first thing
		const redirectToStamps = Util.isEndiciaDomain();

		if (redirectToStamps) {
			return <EndiciaRedirect />;
		}

		const {
			showMask,
			showSignIn,
			optimizelyClient,
			analyticsMetadata,
			LDProvider,
			stylesLoaded,
			auth0Error,
		} = this.state;

		if (!stylesLoaded) {
			return null;
		} else if (showMask) {
			return <Spinner msg="Loading Products" />;
		}

		if (showSignIn) {
			if (auth0Error === false && Auth0Util.isEligibleForAuth0()) {
				Auth0Util.launchSignInPage();
				return null;
			}

			return (
				<Viewport shouldScroll={showSignIn}>
					<ModalContainer>
						<ToastProvider>
							<Router basename={__BASENAME__}>
								<SignIn
									signInCallback={this.signInCallback}
									isAuth0Error={auth0Error}
								/>
							</Router>
						</ToastProvider>
					</ModalContainer>
				</Viewport>
			);
		}

		// render Products main view
		return (
			<ToastProvider>
				<OptimizelyProvider
					optimizely={optimizelyClient}
					user={{
						id: analyticsMetadata.visitor.id.toString(),
						attributes: analyticsMetadata.visitor,
					}}
				>
					{LDProvider ? (
						<LDProvider>
							<MainView />
							<SierraChatLauncher />
						</LDProvider>
					) : (
						<>
							<MainView />
							<SierraChatLauncher />
						</>
					)}
				</OptimizelyProvider>
			</ToastProvider>
		);
	}
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
